import { Injectable, signal, WritableSignal } from '@angular/core';
import { AppHelper } from '@shared-helpers/appHelper';
import { DateConverter } from '@shared-helpers/dateConverter';
import { ConfigurationModel } from '@shared-model/configuration/ConfigurationModel';
import { ConfigurationResult } from '@shared-results/configuration/ConfigurationResult';
import { BasePollingService } from '@shared-services/base-polling-service';
import { Observable, Observer, Subscription } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';

@Injectable({
  providedIn: 'root' // Single instance provided in root
})
export class ConfigurationService extends BasePollingService<ConfigurationResult, ConfigurationModel> {

  private configurationUrl = '/configuration/';

  config: WritableSignal<ConfigurationModel> = signal(null, { equal: AppHelper.objectsEqual });

  constructor() { super("ConfigurationService"); }

  protected override getPollingObservable(): Observable<ConfigurationResult> {
    let url: string = `${this.configurationUrl}getConfiguration`;
    let options = this.httpOptions;
    return this.http?.get<ConfigurationResult>(url, options).pipe(
      timeout(this.httpTimeout),
      map((item) => DateConverter.stringsToDates(item)),
      catchError(this.errorService?.handleHttpErrorAndRethrow<ConfigurationResult>(url, null, options, this)),
      switchMap((result) => {
        return this.handleOperationResult(url, null, options, result, {
          additionalSuccessCriteria: (result) => { return result?.Config ? true : false },
          throwIfNotSuccess: true
        })
      }),
    );
  }

  protected override getPollingInterval(): number {
    return this.config()?.ConfigInterval;
  }

  protected override onPollingError(error: any): void { }

  protected override onPollingResult(result: ConfigurationResult): ConfigurationModel {
    return result?.Config;
  }

  protected override getPollingCachedData(): ConfigurationModel {
    return this.config();
  }

  protected override setPollingCachedData(data: ConfigurationModel): void {
    this.cacheConfigData(data);
  }

  private cacheConfigData(config: ConfigurationModel): void {
    this.config.set(config);
  }

  getConfig(observerOrNext?: Partial<Observer<ConfigurationModel>> | ((value: ConfigurationModel) => void)): Subscription {
    return this.getPollingData(observerOrNext);
  }
}
